import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../API/Auth";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { tokens } from "../theme/tokens";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
    submit: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "oldPassword") setOldPassword(value);
    if (name === "newPassword") setNewPassword(value);
    if (name === "confirmNewPassword") setConfirmNewPassword(value);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let formIsValid = true;
    const newErrors = { oldPassword: "", newPassword: "", confirmNewPassword: "", submit: "" };

    if (!oldPassword) {
      newErrors.oldPassword = "Old password is required";
      formIsValid = false;
    }
    if (!newPassword || newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters long";
      formIsValid = false;
    }
    if (newPassword !== confirmNewPassword) {
      newErrors.confirmNewPassword = "Passwords do not match";
      formIsValid = false;
    }

    setErrors(newErrors);

    if (!formIsValid) {
      return;
    }

    setIsLoading(true);
    try {
      // Get userId from localStorage
      const userData = JSON.parse(localStorage.getItem('userId'));

      const response = await authAPI.changePassword({
        userId: userData,
        oldPassword: oldPassword,
        newPassword: newPassword
      });

      if (response.status && response.code === 200) {
        toast.success("Password changed successfully!");
        // Clear the form
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        
        setTimeout(() => {
          navigate("/"); // Navigate to home or login page after success
        }, 2000);
      } else {
        toast.error(response.message || "Failed to change password. Please try again.");
        setErrors((prev) => ({
          ...prev,
          submit: response.message || "Failed to change password. Please try again.",
        }));
      }
    } catch (error) {
      console.error("Change password error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "An error occurred while changing password",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: tokens.surface,
      }}
    >
      <ToastContainer />
      <Container
        maxWidth="sm"
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh"
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            width: "100%",
            maxWidth: { xs: "100%", sm: 500 },
            borderRadius: { xs: "16px", sm: "20px" },
            backgroundColor: tokens.paper,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
            <img
              src="/images/Wordmark.svg"
              alt="Hotel Logo"
              style={{
                height: 25,
                width: "auto",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              mb: 3,
              fontWeight: 600,
              textAlign: "center",
              background: `linear-gradient(45deg, ${tokens.brand} 30%, ${tokens.accent} 90%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Change Password
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Old Password"
                name="oldPassword"
                type={showOldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={handleChange}
                error={!!errors.oldPassword}
                helperText={errors.oldPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              />
              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmNewPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={handleChange}
                error={!!errors.confirmNewPassword}
                helperText={errors.confirmNewPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              />
            </Stack>

            {errors.submit && (
              <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                {errors.submit}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isLoading}
              sx={{
                mt: 4,
                mb: 3,
                borderRadius: { xs: "25px", sm: "30px" },
                padding: { xs: "12px", sm: "14px" },
                fontSize: { xs: "15px", sm: "16px" },
                fontWeight: 500,
                background: `linear-gradient(45deg, ${tokens.brand} 30%, ${tokens.brandMuted} 90%)`,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: `linear-gradient(45deg, ${tokens.brandMuted} 30%, ${tokens.brandDeep} 90%)`,
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.15)"
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Change Password"
              )}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangePassword;